import React, { useEffect, useState } from "react";
import api, { formatIDR } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, Pencil, Trash2, Eye, X, Route, AlertTriangle, MapPin, CheckCircle2, RotateCcw, History, ReceiptText, Check, Wallet, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { technician_id: "", date: "", amount: "", remark: "", destination: "", new_trip: false };

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" });
  } catch {
    return "";
  }
};

const StatusBadge = ({ status }) =>
  status === "berjalan" ? (
    <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase bg-[#002FA7]/10 text-[#002FA7]">Berjalan</span>
  ) : (
    <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase bg-[#059669]/10 text-[#059669]">Selesai</span>
  );

const UangJalan = () => {
  const [summary, setSummary] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTech, setHistoryTech] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null); // trip view
  const [detailData, setDetailData] = useState({ allocations: [], expenses: [] });
  const [confirmDel, setConfirmDel] = useState(null); // {kind, item}
  const [confirmClose, setConfirmClose] = useState(null); // trip
  const [closeAction, setCloseAction] = useState("petty_cash"); // petty_cash | wa (bila masih ada sisa)
  const [closeNote, setCloseNote] = useState("");
  const [closing, setClosing] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [tripEdit, setTripEdit] = useState(null); // {id, destination, note}

  const loadSummary = () => api.get("/travel-funds/summary").then((r) => setSummary(r.data)).catch(() => toast.error("Gagal memuat data uang jalan"));
  const loadHistory = () =>
    api.get("/travel-trips", { params: { status: "selesai" } }).then((r) => setHistory(r.data)).catch(() => toast.error("Gagal memuat riwayat perjalanan"));
  const loadDetail = (tripId) =>
    api.get("/travel-funds", { params: { trip_id: tripId } }).then((r) => setDetailData(r.data)).catch(() => toast.error("Gagal memuat detail"));
  const reloadTrip = (tripId) =>
    api.get("/travel-trips").then((r) => {
      const t = r.data.find((x) => x.id === tripId);
      if (t) setDetail(t);
    });

  useEffect(() => { loadSummary(); loadHistory(); }, []);

  const refresh = () => {
    loadSummary();
    loadHistory();
    if (detail) { loadDetail(detail.id); reloadTrip(detail.id); }
  };

  const openDetail = (trip) => {
    setDetail(trip);
    setDetailData({ allocations: [], expenses: [] });
    loadDetail(trip.id);
  };

  const openCreate = (techId = "") => {
    setForm({ ...emptyForm, technician_id: techId, date: todayStr() });
    setEditingId(null);
    setModal(true);
  };
  const openEdit = (a) => {
    setForm({ ...emptyForm, technician_id: a.technician_id, date: a.date, amount: a.amount, remark: a.remark || "" });
    setEditingId(a.id);
    setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: Number(form.amount) || 0, destination: (form.destination || "").trim() };
    if (!payload.technician_id) { toast.error("Pilih teknisi"); return; }
    if (payload.amount <= 0) { toast.error("Jumlah harus lebih dari 0"); return; }
    if (!editingId && (!activeTripOfForm || payload.new_trip) && !payload.destination) { toast.error("Isi tujuan perjalanan"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/travel-funds/${editingId}`, payload);
        toast.success("Uang jalan diperbarui");
      } else {
        await api.post("/travel-funds", payload);
        toast.success(payload.new_trip || !activeTripOfForm ? `Perjalanan baru "${payload.destination}" dimulai` : "Uang jalan ditambahkan");
      }
      setModal(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan uang jalan");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      if (confirmDel.kind === "allocation") await api.delete(`/travel-funds/${confirmDel.item.id}`);
      else if (confirmDel.kind === "trip") await api.delete(`/travel-trips/${confirmDel.item.id}`);
      else await api.delete(`/travel-funds/expenses/${confirmDel.item.id}`);
      toast.success("Data dihapus");
      if (confirmDel.kind === "trip") setDetail(null);
      setConfirmDel(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const decideExpense = async (x, action) => {
    if (action === "reject" && !window.confirm(`Tolak pemakaian "${x.purpose}" ${formatIDR(x.amount)}? Nilai pemakaian akan dihapus.`)) return;
    try {
      await api.post(`/travel-expense-approvals/${x.id}`, { action });
      toast.success(action === "approve" ? "Pemakaian disetujui" : "Pemakaian ditolak & dihapus");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan keputusan");
    }
  };

  const openClose = (trip) => {
    setCloseAction("petty_cash");
    setCloseNote("");
    setConfirmClose(trip);
  };

  const closeTrip = async () => {
    if (!confirmClose || closing) return;
    const hasLeftover = Math.round(confirmClose.balance) > 0;
    const action = hasLeftover ? closeAction : "";
    setClosing(true);
    try {
      const r = await api.post(`/travel-trips/${confirmClose.id}/close`, { action, note: closeNote.trim() });
      if (action === "wa") toast.success(`WA sisa uang jalan dikirim ke ${confirmClose.technician_name} — perjalanan tetap berjalan`);
      else if (action === "petty_cash") toast.success(`Perjalanan "${confirmClose.destination}" selesai · ${formatIDR(confirmClose.balance)} masuk Petty Cash`);
      else toast.success(`Perjalanan "${confirmClose.destination}" selesai`);
      if (detail && detail.id === confirmClose.id) setDetail(r.data);
      setConfirmClose(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyelesaikan perjalanan");
    } finally {
      setClosing(false);
    }
  };

  const emailTrip = async (trip) => {
    setEmailing(true);
    try {
      const r = await api.post(`/travel-trips/${trip.id}/email`);
      toast.success(`Laporan perjalanan dikirim ke ${r.data.sent_to}`);
      reloadTrip(trip.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal kirim email");
    } finally {
      setEmailing(false);
    }
  };

  const reopenTrip = async (trip) => {
    try {
      const r = await api.post(`/travel-trips/${trip.id}/reopen`);
      toast.success(`Perjalanan "${trip.destination}" dibuka lagi`);
      if (detail && detail.id === trip.id) setDetail(r.data);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuka perjalanan");
    }
  };

  const saveTripEdit = async (e) => {
    e.preventDefault();
    if (!tripEdit.destination.trim()) { toast.error("Tujuan perjalanan harus diisi"); return; }
    try {
      const r = await api.put(`/travel-trips/${tripEdit.id}`, { destination: tripEdit.destination.trim(), note: tripEdit.note || "" });
      toast.success("Tujuan perjalanan diperbarui");
      if (detail && detail.id === tripEdit.id) setDetail(r.data);
      setTripEdit(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const activeRows = summary.filter((r) => r.active_trip);
  const totalGiven = activeRows.reduce((s, r) => s + (r.total_given || 0), 0);
  const totalUsed = activeRows.reduce((s, r) => s + (r.total_used || 0), 0);
  const techs = [...summary].sort((a, b) => a.name.localeCompare(b.name));
  const selectedTech = summary.find((t) => t.technician_id === form.technician_id);
  const activeTripOfForm = selectedTech?.active_trip || null;
  const historyRows = historyTech ? history.filter((t) => t.technician_id === historyTech) : history;
  const historyTechs = [...new Map(history.map((t) => [t.technician_id, t.technician_name])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const needDestination = !editingId && (!activeTripOfForm || form.new_trip);

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <div className="label-cap mb-2">Keuangan Karyawan</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Uang Jalan Tim</h1>
        </div>
        <button onClick={() => openCreate()} data-testid="add-travel-fund-button" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
          <Plus size={14} /> Input Uang Jalan
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 max-w-3xl">
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="label-cap mb-1">Diberikan (berjalan)</div>
          <div className="font-mono font-bold text-xl" data-testid="travel-total-given">{formatIDR(totalGiven)}</div>
        </div>
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="label-cap mb-1">Dipakai (berjalan)</div>
          <div className="font-mono font-bold text-xl text-[#D97706]" data-testid="travel-total-used">{formatIDR(totalUsed)}</div>
        </div>
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="label-cap mb-1">Sisa Saldo Berjalan</div>
          <div className={`font-mono font-bold text-xl ${totalGiven - totalUsed < 0 ? "text-[#DC2626]" : "text-[#059669]"}`} data-testid="travel-total-balance">{formatIDR(totalGiven - totalUsed)}</div>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] mb-6">
        <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2">
          <Route size={14} className="text-[#52525B]" />
          <span className="text-sm font-bold">Perjalanan Berjalan</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="border-b border-[#E4E4E7]">
                <th className="text-left label-cap px-4 py-3">Teknisi</th>
                <th className="text-left label-cap px-4 py-3">Tujuan Perjalanan</th>
                <th className="text-right label-cap px-4 py-3">Diberikan</th>
                <th className="text-right label-cap px-4 py-3">Dipakai</th>
                <th className="text-right label-cap px-4 py-3">Sisa</th>
                <th className="text-left label-cap px-4 py-3">Aktivitas Terakhir</th>
                <th className="text-right label-cap px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {activeRows.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-sm text-[#52525B]">Tidak ada perjalanan berjalan. Klik "Input Uang Jalan" untuk memulai perjalanan baru.</td></tr>
              )}
              {activeRows.map((r) => (
                <tr key={r.technician_id} className="border-b border-[#E4E4E7] row-hover" data-testid={`travel-row-${r.technician_id}`}>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-bold">{r.name}</div>
                    {!r.nik && <div className="text-[10px] text-[#D97706] flex items-center gap-1"><AlertTriangle size={10} /> NIK kosong — tidak tampil di absensi</div>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-1.5 font-bold"><MapPin size={12} className="text-[#002FA7] shrink-0" />{r.active_trip.destination}</div>
                    <div className="text-[10px] text-[#52525B] font-mono">sejak {r.active_trip.start_date}</div>
                    {r.active_trip.pending_count > 0 && (
                      <div className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase bg-[#D97706]/10 text-[#D97706]">
                        <ReceiptText size={10} /> {r.active_trip.pending_count} tanpa bon · butuh approval
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-right">{formatIDR(r.total_given)}</td>
                  <td className="px-4 py-3 text-sm font-mono text-right text-[#D97706]">{formatIDR(r.total_used)}</td>
                  <td className={`px-4 py-3 text-sm font-mono text-right font-bold ${r.balance < 0 ? "text-[#DC2626]" : "text-[#059669]"}`} data-testid={`travel-balance-${r.technician_id}`}>{formatIDR(r.balance)}</td>
                  <td className="px-4 py-3 text-sm font-mono">{r.last_activity || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openCreate(r.technician_id)} title="Tambah uang jalan" data-testid={`travel-add-${r.technician_id}`} className="px-2 py-1.5 text-[10px] font-bold uppercase hover:bg-[#002FA7]/10 text-[#002FA7] border border-[#E4E4E7]">+ Uang Jalan</button>
                      <button onClick={() => openDetail(r.active_trip)} title="Lihat detail" data-testid={`travel-detail-${r.technician_id}`} className="px-2 py-1.5 text-[10px] font-bold uppercase hover:bg-[#F4F4F5] border border-[#E4E4E7] flex items-center gap-1"><Eye size={12} /> Detail</button>
                      <button onClick={() => openClose(r.active_trip)} title="Tandai perjalanan selesai" data-testid={`travel-close-${r.technician_id}`} className="px-2 py-1.5 text-[10px] font-bold uppercase hover:bg-[#059669]/10 text-[#059669] border border-[#E4E4E7] flex items-center gap-1"><CheckCircle2 size={12} /> Selesai</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7]">
        <div className="px-4 py-3 border-b border-[#E4E4E7] flex flex-wrap items-center gap-2">
          <History size={14} className="text-[#52525B]" />
          <span className="text-sm font-bold flex-1">Riwayat Perjalanan Selesai</span>
          <select value={historyTech} onChange={(e) => setHistoryTech(e.target.value)} data-testid="travel-history-filter" className="px-2 py-1.5 border border-[#E4E4E7] text-xs outline-none focus:border-[#002FA7] bg-white">
            <option value="">Semua teknisi</option>
            {historyTechs.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="border-b border-[#E4E4E7]">
                <th className="text-left label-cap px-4 py-3">Teknisi</th>
                <th className="text-left label-cap px-4 py-3">Tujuan Perjalanan</th>
                <th className="text-left label-cap px-4 py-3">Periode</th>
                <th className="text-right label-cap px-4 py-3">Diberikan</th>
                <th className="text-right label-cap px-4 py-3">Dipakai</th>
                <th className="text-right label-cap px-4 py-3">Sisa</th>
                <th className="text-right label-cap px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-sm text-[#52525B]">Belum ada perjalanan yang selesai.</td></tr>
              )}
              {historyRows.map((t) => (
                <tr key={t.id} className="border-b border-[#E4E4E7] row-hover" data-testid={`travel-trip-${t.id}`}>
                  <td className="px-4 py-3 text-sm font-bold">{t.technician_name}</td>
                  <td className="px-4 py-3 text-sm">{t.destination}</td>
                  <td className="px-4 py-3 text-xs font-mono">{t.start_date} – {t.end_date || "?"}</td>
                  <td className="px-4 py-3 text-sm font-mono text-right">{formatIDR(t.total_given)}</td>
                  <td className="px-4 py-3 text-sm font-mono text-right text-[#D97706]">{formatIDR(t.total_used)}</td>
                  <td className={`px-4 py-3 text-sm font-mono text-right font-bold ${t.balance < 0 ? "text-[#DC2626]" : t.balance > 0 ? "text-[#059669]" : ""}`}>{formatIDR(t.balance)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openDetail(t)} title="Lihat detail" className="px-2 py-1.5 text-[10px] font-bold uppercase hover:bg-[#F4F4F5] border border-[#E4E4E7] inline-flex items-center gap-1"><Eye size={12} /> Detail</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40" onClick={() => setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] w-full max-w-4xl max-h-[90vh] flex flex-col" data-testid="travel-detail-modal">
            <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="label-cap flex items-center gap-2">Detail Perjalanan · {detail.technician_name} <StatusBadge status={detail.status} /></div>
                <h2 className="font-heading text-2xl font-bold flex items-center gap-2 break-words">
                  <MapPin size={18} className="text-[#002FA7] shrink-0" />{detail.destination}
                  <button onClick={() => setTripEdit({ id: detail.id, destination: detail.destination, note: detail.note || "" })} title="Ubah tujuan" className="p-1 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><Pencil size={12} /></button>
                </h2>
                <div className="text-xs text-[#52525B] mt-1 font-mono">{detail.start_date} – {detail.status === "selesai" ? detail.end_date : "sekarang"}</div>
                {detail.note && <div className="text-xs text-[#52525B] mt-0.5">{detail.note}</div>}
                <div className="text-xs text-[#52525B] mt-1 flex flex-wrap gap-x-4">
                  <span>Diberikan <b className="font-mono text-[#09090B]">{formatIDR(detail.total_given)}</b></span>
                  <span>Dipakai <b className="font-mono text-[#D97706]">{formatIDR(detail.total_used)}</b></span>
                  {detail.returned > 0 && <span>Ke Petty Cash <b className="font-mono text-[#002FA7]">{formatIDR(detail.returned)}</b></span>}
                  <span>Sisa <b className={`font-mono ${detail.balance < 0 ? "text-[#DC2626]" : "text-[#059669]"}`}>{formatIDR(detail.balance)}</b></span>
                </div>
                {(detail.leftover_reminders || []).length > 0 && (
                  <div className="text-[10px] text-[#52525B] mt-1 flex items-center gap-1">
                    <MessageCircle size={11} /> WA sisa uang jalan terkirim {detail.leftover_reminders.length}x · terakhir {fmtDateTime(detail.leftover_reminders[detail.leftover_reminders.length - 1].at)}
                  </div>
                )}
                {detail.status === "selesai" && (
                  <div className="text-[10px] mt-1 flex items-center gap-1 text-[#52525B]">
                    <Mail size={11} />
                    {detail.report_email?.sent_at ? (
                      <span>Laporan email terkirim ke {detail.report_email.to} · {fmtDateTime(detail.report_email.sent_at)}</span>
                    ) : detail.report_email?.error ? (
                      <span className="text-[#DC2626]">Email laporan gagal: {detail.report_email.error}</span>
                    ) : (
                      <span>Laporan email belum terkirim</span>
                    )}
                    <button onClick={() => emailTrip(detail)} disabled={emailing} className="ml-1 font-bold uppercase text-[#002FA7] disabled:opacity-50">{emailing ? "Mengirim…" : "Kirim ulang"}</button>
                  </div>
                )}
              </div>
              <div className="flex items-start gap-1 shrink-0">
                {detail.status === "berjalan" ? (
                  <button onClick={() => openClose(detail)} className="px-2 py-1.5 text-[10px] font-bold uppercase hover:bg-[#059669]/10 text-[#059669] border border-[#E4E4E7] flex items-center gap-1"><CheckCircle2 size={12} /> Selesai</button>
                ) : (
                  <button onClick={() => reopenTrip(detail)} className="px-2 py-1.5 text-[10px] font-bold uppercase hover:bg-[#F4F4F5] border border-[#E4E4E7] flex items-center gap-1"><RotateCcw size={12} /> Buka Lagi</button>
                )}
                {detailData.allocations.length === 0 && detailData.expenses.length === 0 && (
                  <button onClick={() => setConfirmDel({ kind: "trip", item: detail })} title="Hapus perjalanan kosong" className="p-1.5 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={12} /></button>
                )}
                <button onClick={() => setDetail(null)} className="p-2 hover:bg-[#F4F4F5]"><X size={16} /></button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="label-cap">Uang Jalan Diberikan</div>
                  {detail.status === "berjalan" && (
                    <button onClick={() => openCreate(detail.technician_id)} className="text-[10px] font-bold uppercase text-[#002FA7] flex items-center gap-1"><Plus size={12} /> Tambah</button>
                  )}
                </div>
                {detailData.allocations.length === 0 ? (
                  <div className="text-sm text-[#52525B] border border-dashed border-[#E4E4E7] p-4">Belum ada uang jalan.</div>
                ) : (
                  <div className="border border-[#E4E4E7] divide-y divide-[#E4E4E7]">
                    {detailData.allocations.map((a) => (
                      <div key={a.id} className="px-3 py-2 flex items-start gap-3" data-testid={`travel-allocation-${a.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-xs text-[#52525B]">{a.date}</span>
                            <span className="font-mono font-bold text-sm">{formatIDR(a.amount)}</span>
                          </div>
                          {a.remark && <div className="text-xs text-[#52525B] mt-0.5 break-words">{a.remark}</div>}
                        </div>
                        <button onClick={() => openEdit(a)} title="Edit" className="p-1.5 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><Pencil size={12} /></button>
                        <button onClick={() => setConfirmDel({ kind: "allocation", item: a })} title="Hapus" className="p-1.5 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="label-cap mb-2">Pemakaian (diinput teknisi)</div>
                {detailData.expenses.length === 0 ? (
                  <div className="text-sm text-[#52525B] border border-dashed border-[#E4E4E7] p-4">Belum ada pemakaian yang diinput.</div>
                ) : (
                  <div className="border border-[#E4E4E7] divide-y divide-[#E4E4E7]">
                    {detailData.expenses.map((x) => (
                      <div key={x.id} className="px-3 py-2 flex items-start gap-3" data-testid={`travel-expense-${x.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-xs text-[#52525B]">{x.date}</span>
                            <span className="font-mono font-bold text-sm text-[#D97706]">{formatIDR(x.amount)}</span>
                          </div>
                          <div className="text-xs text-[#09090B] mt-0.5 break-words">{x.purpose}</div>
                          {x.has_receipt && x.receipt_url ? (
                            <div className="mt-1 flex items-start gap-2">
                              <a href={x.receipt_url} target="_blank" rel="noreferrer" title="Lihat struk" className="shrink-0">
                                <img src={x.receipt_url} alt="Struk" loading="lazy" className="w-12 h-12 object-cover border border-[#E4E4E7]" />
                              </a>
                              {x.receipt_ocr ? (
                                <div className="text-[10px] text-[#52525B] min-w-0">
                                  <div className="break-words"><b>Terbaca:</b> {x.receipt_ocr.merchant || "-"}{x.receipt_ocr.date ? ` · ${x.receipt_ocr.date}` : ""}</div>
                                  {x.receipt_ocr.amount > 0 && (
                                    <div className={Math.abs(x.receipt_ocr.amount - x.amount) > 1 ? "text-[#DC2626] font-bold" : ""}>
                                      Total struk {formatIDR(x.receipt_ocr.amount)}
                                      {Math.abs(x.receipt_ocr.amount - x.amount) > 1 && " — beda dengan input!"}
                                    </div>
                                  )}
                                  {x.receipt_ocr.is_receipt === false && <div className="text-[#DC2626] font-bold">Foto tidak terdeteksi sebagai struk</div>}
                                </div>
                              ) : (
                                <div className="text-[10px] text-[#52525B]">Struk terlampir</div>
                              )}
                            </div>
                          ) : x.has_receipt === false ? (
                            <div className="mt-1 text-[10px]">
                              <span className={`inline-block px-1.5 py-0.5 font-bold uppercase ${x.approval_status === "pending" ? "bg-[#D97706]/10 text-[#D97706]" : "bg-[#059669]/10 text-[#059669]"}`}>
                                Tanpa bon · {x.approval_status === "pending" ? "menunggu approval" : `disetujui${x.approved_by ? ` ${x.approved_by}` : ""}`}
                              </span>
                              <div className="text-[#52525B] mt-0.5 break-words">Alasan: {x.no_receipt_reason || "-"}</div>
                            </div>
                          ) : null}
                          <div className="text-[10px] text-[#A1A1AA] mt-0.5">Diinput {fmtDateTime(x.created_at)}</div>
                        </div>
                        {x.approval_status === "pending" && (
                          <>
                            <button onClick={() => decideExpense(x, "approve")} title="Setujui pemakaian tanpa bon" data-testid={`travel-expense-approve-${x.id}`} className="p-1.5 hover:bg-[#059669]/10 text-[#059669] border border-[#059669]/40"><Check size={12} /></button>
                            <button onClick={() => decideExpense(x, "reject")} title="Tolak & hapus pemakaian" data-testid={`travel-expense-reject-${x.id}`} className="p-1.5 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#DC2626]/40"><X size={12} /></button>
                          </>
                        )}
                        <button onClick={() => setConfirmDel({ kind: "expense", item: x })} title="Hapus" className="p-1.5 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[#E4E4E7] w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="travel-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <div className="label-cap">{editingId ? "Ubah" : "Input"} Data</div>
              <h2 className="font-heading text-2xl font-bold">Uang Jalan</h2>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="label-cap block mb-1.5">Teknisi<span className="text-[#DC2626]">*</span></label>
                <select required disabled={!!editingId} data-testid="travel-tech-select" value={form.technician_id} onChange={(e) => setForm({ ...form, technician_id: e.target.value, new_trip: false, destination: "" })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white disabled:bg-[#F4F4F5]">
                  <option value="">— Pilih Teknisi —</option>
                  {techs.map((t) => <option key={t.technician_id} value={t.technician_id}>{t.name}{t.nik ? "" : " (NIK kosong)"}{t.active_trip ? ` · ${t.active_trip.destination}` : ""}</option>)}
                </select>
                {selectedTech && !selectedTech.nik && (
                  <p className="text-[11px] text-[#D97706] mt-1">Teknisi ini belum punya NIK, jadi uang jalan tidak akan tampil di absensi. Isi NIK di menu Karyawan.</p>
                )}
              </div>

              {!editingId && form.technician_id && (
                <div className="col-span-2">
                  <label className="label-cap block mb-1.5">Tujuan Perjalanan{needDestination && <span className="text-[#DC2626]">*</span>}</label>
                  {activeTripOfForm && (
                    <div className="border border-[#E4E4E7] mb-2">
                      <label className="flex items-start gap-2 px-3 py-2 cursor-pointer">
                        <input type="radio" checked={!form.new_trip} onChange={() => setForm({ ...form, new_trip: false })} className="mt-1" />
                        <span className="text-sm">
                          Tambah ke perjalanan berjalan: <b>{activeTripOfForm.destination}</b>
                          <span className="block text-[11px] text-[#52525B] font-mono">sejak {activeTripOfForm.start_date} · sisa {formatIDR(activeTripOfForm.balance)}</span>
                        </span>
                      </label>
                      <label className="flex items-start gap-2 px-3 py-2 cursor-pointer border-t border-[#E4E4E7]">
                        <input type="radio" checked={form.new_trip} onChange={() => setForm({ ...form, new_trip: true })} className="mt-1" data-testid="travel-new-trip-radio" />
                        <span className="text-sm">Selesaikan perjalanan itu & mulai tujuan baru</span>
                      </label>
                    </div>
                  )}
                  {needDestination && (
                    <input required data-testid="travel-destination-input" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" placeholder="Mis. Medan – Siantar" />
                  )}
                  {!activeTripOfForm && <p className="text-[11px] text-[#52525B] mt-1">Teknisi ini tidak punya perjalanan berjalan — uang jalan ini memulai perjalanan baru.</p>}
                </div>
              )}

              <div>
                <label className="label-cap block mb-1.5">Tanggal<span className="text-[#DC2626]">*</span></label>
                <input type="date" required data-testid="travel-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" />
              </div>
              <div>
                <label className="label-cap block mb-1.5">Jumlah (IDR)<span className="text-[#DC2626]">*</span></label>
                <input type="number" required min="1" data-testid="travel-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] font-mono" placeholder="200000" />
              </div>
              <div className="col-span-2">
                <label className="label-cap block mb-1.5">Remark</label>
                <textarea rows={3} data-testid="travel-remark-input" value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" placeholder="Mis. Uang jalan 3 hari" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" disabled={saving} data-testid="travel-submit-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white hover:bg-[#00227A] disabled:opacity-60">{saving ? "Menyimpan…" : "Simpan"}</button>
            </div>
          </form>
        </div>
      )}

      {tripEdit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setTripEdit(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveTripEdit} className="bg-white border border-[#E4E4E7] w-full max-w-md">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <div className="label-cap">Ubah</div>
              <h2 className="font-heading text-2xl font-bold">Tujuan Perjalanan</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="label-cap block mb-1.5">Tujuan<span className="text-[#DC2626]">*</span></label>
                <input required value={tripEdit.destination} onChange={(e) => setTripEdit({ ...tripEdit, destination: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" />
              </div>
              <div>
                <label className="label-cap block mb-1.5">Catatan</label>
                <textarea rows={2} value={tripEdit.note} onChange={(e) => setTripEdit({ ...tripEdit, note: e.target.value })} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7]" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setTripEdit(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white hover:bg-[#00227A]">Simpan</button>
            </div>
          </form>
        </div>
      )}

      {confirmClose && (() => {
        const sisa = Math.round(confirmClose.balance);
        const hasLeftover = sisa > 0;
        return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => !closing && setConfirmClose(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-md w-full p-6" data-testid="close-trip-modal">
            <h3 className="font-heading text-lg font-bold mb-2">Perjalanan Selesai?</h3>
            <p className="text-sm text-[#52525B] mb-3">
              Perjalanan <b>{confirmClose.destination}</b> ({confirmClose.technician_name})
            </p>
            <div className="border border-[#E4E4E7] text-sm divide-y divide-[#E4E4E7] mb-4">
              <div className="px-3 py-1.5 flex justify-between"><span className="text-[#52525B]">Diberikan</span><b className="font-mono">{formatIDR(confirmClose.total_given)}</b></div>
              <div className="px-3 py-1.5 flex justify-between"><span className="text-[#52525B]">Terpakai (dilaporkan)</span><b className="font-mono text-[#D97706]">{formatIDR(confirmClose.total_used)}</b></div>
              <div className="px-3 py-1.5 flex justify-between"><span className="text-[#52525B]">{sisa < 0 ? "Kurang (pemakaian lebih besar)" : "Sisa uang jalan"}</span><b className={`font-mono ${sisa < 0 ? "text-[#DC2626]" : "text-[#059669]"}`}>{formatIDR(Math.abs(sisa))}</b></div>
            </div>
            {confirmClose.pending_count > 0 && (
              <div className="text-xs text-[#D97706] mb-3 flex items-start gap-1"><AlertTriangle size={13} className="shrink-0 mt-0.5" /> {confirmClose.pending_count} pemakaian tanpa bon masih menunggu approval.</div>
            )}
            {hasLeftover ? (
              <>
                <div className="flex items-start gap-2 text-xs bg-[#D97706]/10 text-[#92400E] px-3 py-2 mb-3">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>Masih ada sisa <b className="font-mono">{formatIDR(sisa)}</b> dari uang jalan. Pilih tindakan:</span>
                </div>
                <div className="space-y-2 mb-3">
                  <label className={`flex items-start gap-2 border px-3 py-2 cursor-pointer ${closeAction === "petty_cash" ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#E4E4E7]"}`}>
                    <input type="radio" name="close-action" checked={closeAction === "petty_cash"} onChange={() => setCloseAction("petty_cash")} className="mt-1 accent-[#002FA7]" data-testid="close-action-petty-cash" />
                    <div>
                      <div className="text-sm font-bold flex items-center gap-1"><Wallet size={14} /> Masukkan ke Petty Cash</div>
                      <div className="text-[11px] text-[#52525B]">Sisa {formatIDR(sisa)} dicatat sebagai dana masuk Petty Cash (remark: pengembalian sisa uang jalan {confirmClose.technician_name} - {confirmClose.destination}). Perjalanan langsung selesai.</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2 border px-3 py-2 cursor-pointer ${closeAction === "wa" ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#E4E4E7]"}`}>
                    <input type="radio" name="close-action" checked={closeAction === "wa"} onChange={() => setCloseAction("wa")} className="mt-1 accent-[#002FA7]" data-testid="close-action-wa" />
                    <div>
                      <div className="text-sm font-bold flex items-center gap-1"><MessageCircle size={14} /> Kirim WA ke {confirmClose.technician_name}</div>
                      <div className="text-[11px] text-[#52525B]">Info sisa uang jalan agar dikembalikan atau diinput laporan pengeluarannya di absensi. Perjalanan <b>tetap berjalan</b> — klik Selesai lagi setelah beres.</div>
                      {(confirmClose.leftover_reminders || []).length > 0 && (
                        <div className="text-[10px] text-[#D97706] mt-0.5">Sudah pernah dikirim {confirmClose.leftover_reminders.length}x (terakhir {fmtDateTime(confirmClose.leftover_reminders[confirmClose.leftover_reminders.length - 1].at)})</div>
                      )}
                    </div>
                  </label>
                </div>
                <textarea value={closeNote} onChange={(e) => setCloseNote(e.target.value)} rows={2} placeholder={closeAction === "wa" ? "Catatan tambahan di WA (opsional)" : "Remark tambahan (opsional)"} className="w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] mb-4" data-testid="close-trip-note" />
              </>
            ) : (
              <p className="text-sm text-[#52525B] mb-4">Perjalanan akan ditandai selesai dan dipindah ke riwayat. Pemakaian baru dari absensi tidak bisa diinput sampai ada uang jalan dengan tujuan baru.</p>
            )}
            <p className="text-[10px] text-[#A1A1AA] mb-4 flex items-center gap-1"><Mail size={11} /> Saat perjalanan selesai, laporan perjalanan & pemakaian uang jalan dikirim otomatis ke email laporan.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmClose(null)} disabled={closing} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={closeTrip} disabled={closing} data-testid="confirm-close-trip-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#059669] text-white disabled:opacity-60">
                {closing ? "Memproses…" : hasLeftover && closeAction === "wa" ? "Kirim WA" : hasLeftover ? "Masukkan & Selesai" : "Ya, Selesai"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">{confirmDel.kind === "allocation" ? "Hapus Uang Jalan?" : confirmDel.kind === "trip" ? "Hapus Perjalanan?" : "Hapus Pemakaian?"}</h3>
            <p className="text-sm text-[#52525B] mb-6">
              {confirmDel.kind === "allocation"
                ? `Uang jalan ${formatIDR(confirmDel.item.amount)} tanggal ${confirmDel.item.date} akan dihapus permanen.`
                : confirmDel.kind === "trip"
                ? `Perjalanan "${confirmDel.item.destination}" (kosong) akan dihapus.`
                : `Pemakaian ${formatIDR(confirmDel.item.amount)} (${confirmDel.item.purpose}) tanggal ${confirmDel.item.date} akan dihapus permanen.`}
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} data-testid="confirm-delete-travel-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UangJalan;
